/* eslint-disable react/no-unescaped-entities */

"use client";

import { useState } from "react";

import { SiteFooter } from "@/components/SiteFooter";
import { SiteHeader } from "@/components/SiteHeader";

import styles from "../legal.module.css";

type ContactMethod = "email" | "whatsapp";

export default function ContactPage() {
  const [contactMethod, setContactMethod] = useState<ContactMethod>("email");
  const [contact, setContact] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL?.trim();
  const apiRoot = apiBaseUrl ? `${apiBaseUrl.replace(/\/+$/, "")}/pharmacy` : "";

  const contactLabel = contactMethod === "email" ? "Votre adresse e-mail" : "Votre numéro WhatsApp";
  const contactPlaceholder =
    contactMethod === "email" ? "exemple@pharmacie.com" : "+243 000 000 000";

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!apiBaseUrl) {
      setStatus("error");
      setFeedback("Impossible d'envoyer votre message pour le moment. Veuillez réessayer un peu plus tard.");
      return;
    }

    setStatus("submitting");
    setFeedback("");

    try {
      const response = await fetch(`${apiRoot}/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          contactType: contactMethod,
          contact: contact.trim(),
          message: message.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error("Contact request failed");
      }

      setStatus("success");
      setFeedback("Votre message a bien été envoyé. Nous vous recontacterons dans les meilleurs délais.");
      setContact("");
      setMessage("");
    } catch {
      setStatus("error");
      setFeedback("Impossible d'envoyer votre message pour le moment. Veuillez réessayer un peu plus tard.");
    }
  };

  return (
    <>
      <SiteHeader />
      <div className={styles.page}>
      <main className={styles.shell}>
        <section className={styles.hero}>
          <div className={styles.heroCard}>
            <span className={styles.eyebrow}>Nous contacter</span>
            <h1 className={styles.heroTitle}>Un point de contact simple pour les questions sérieuses.</h1>
            <p className={styles.heroText}>
              Si vous avez une question commerciale, un besoin de démonstration, un doute avant souscription ou
              une demande liée à votre exploitation, laissez-nous votre contact et votre message. Nous pourrons ainsi
              vous répondre de manière claire, rapide et adaptée à votre besoin.
            </p>
          </div>

          <aside className={styles.heroAside}>
            <div className={styles.metaCard}>
              <strong>Canal au choix</strong>
              <p>Le visiteur peut choisir entre une adresse e-mail et un contact WhatsApp selon sa préférence.</p>
            </div>
            <div className={styles.metaCard}>
              <strong>Échange plus simple</strong>
              <p>Décrivez votre besoin en quelques lignes utiles afin d'obtenir une réponse plus rapide et plus pertinente.</p>
            </div>
          </aside>
        </section>

        <section className={styles.section}>
          <div className={styles.formLayout}>
            <form className={styles.formCard} onSubmit={handleSubmit}>
              <span className={styles.eyebrow}>Formulaire</span>
              <h2 className={styles.sectionTitle}>Laissez un message clair, nous pourrons vous recontacter proprement.</h2>

              <div className={styles.formStack}>
                <div className={styles.field}>
                  <label>Canal de contact</label>
                  <div className={styles.toggleGroup}>
                    <button
                      type="button"
                      className={`${styles.toggleButton} ${contactMethod === "email" ? styles.toggleButtonActive : ""}`}
                      onClick={() => setContactMethod("email")}
                    >
                      E-mail
                    </button>
                    <button
                      type="button"
                      className={`${styles.toggleButton} ${contactMethod === "whatsapp" ? styles.toggleButtonActive : ""}`}
                      onClick={() => setContactMethod("whatsapp")}
                    >
                      WhatsApp
                    </button>
                  </div>
                </div>

                <div className={styles.field}>
                  <label htmlFor="contact-value">{contactLabel}</label>
                  <input
                    id="contact-value"
                    type={contactMethod === "email" ? "email" : "tel"}
                    value={contact}
                    onChange={(event) => setContact(event.target.value)}
                    placeholder={contactPlaceholder}
                    className={styles.input}
                    required
                  />
                  <p className={styles.fieldHint}>
                    Indiquez un contact que vous consultez réellement afin de faciliter le retour de l'équipe.
                  </p>
                </div>

                <div className={styles.field}>
                  <label htmlFor="contact-message">Votre message</label>
                  <textarea
                    id="contact-message"
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    placeholder="Décrivez votre besoin, votre contexte ou votre question en quelques lignes utiles."
                    className={styles.textarea}
                    required
                  />
                </div>

                <div className={styles.submitRow}>
                  <button type="submit" className={styles.submitButton} disabled={status === "submitting"}>
                    {status === "submitting" ? "Envoi en cours..." : "Envoyer le message"}
                  </button>

                  {feedback ? (
                    <p className={`${styles.status} ${status === "success" ? styles.statusSuccess : styles.statusError}`}>
                      {feedback}
                    </p>
                  ) : null}
                </div>
              </div>
            </form>

            <aside className={styles.contactAside}>
              <div className={styles.noteCard}>
                <strong>Pour un bon traitement</strong>
                <p>Un message court mais concret est souvent plus utile qu'une demande trop vague. Précisez votre besoin, votre type de pharmacie et ce que vous attendez.</p>
              </div>
              <div className={styles.noteCard}>
                <strong>Cas fréquents</strong>
                <p>Démonstration du produit, question sur les abonnements, création de succursales, conditions d'utilisation, accompagnement au démarrage ou intégration future.</p>
              </div>
              <div className={styles.noteCard}>
                <strong>Réponse plus efficace</strong>
                <p>Plus votre message est précis, plus il sera facile de vous orienter rapidement vers la bonne solution.</p>
              </div>
            </aside>
          </div>
        </section>
      </main>
      </div>
      <SiteFooter />
    </>
  );
}
